import type {ActivityVersion, CompetenceResult, StudentAttempt} from "./activity-library";

export type AttendanceOverride = CompetenceResult & {source: "attendance_auto"};
export type AttendanceRecord = {absent: boolean; updatedAt: string; results: Record<string, AttendanceOverride>};

export function resultsForMember(attempt: StudentAttempt, studentId: string) {
  return {...attempt.competenceResults, ...attempt.attendance?.[studentId]?.results};
}

/** Only automatic results are removed on return; common teacher validations are untouched. */
export function setMemberAttendance(attempt: StudentAttempt, version: ActivityVersion, studentId: string, absent: boolean, now: string) {
  const previous = attempt.attendance?.[studentId];
  if (previous?.absent === absent || (!previous && !absent)) return false;
  const results: Record<string, AttendanceOverride> = {};
  if (absent) for (const cp of version.checkpoints) {
    // An existing NE is an explicit teacher validation, unlike an empty UI selector.
    if (!attempt.competenceResults[cp.id]) results[cp.id] = {
      checkpointId: cp.id, competencyId: cp.competencyId, level: "ABS", comment: "Absence en séance",
      validatedAt: now, source: "attendance_auto",
    };
  }
  (attempt.attendance ??= {})[studentId] = {absent, updatedAt: now, results};
  return true;
}

export function hasProduction(attempt: StudentAttempt) {
  return Object.values(attempt.answers).some(value => {
    if (!value.trim()) return false;
    try {
      const parsed = JSON.parse(value);
      if(parsed?.format==='resource-measures-v1')return typeof parsed.text==='string'&&!!parsed.text.trim() || (Array.isArray(parsed.rows)&&parsed.rows.some((r:any)=>r?.source==='simulation'&&Number.isFinite(r.measurement?.measurementIndex)||r?.source==='manual'&&Object.entries(r).some(([k,v])=>k!=='source'&&typeof v==='string'&&v.trim())));
      if (Array.isArray(parsed)) return parsed.some(row => row && typeof row === "object"
        ? Object.values(row).some(cell => cell != null && String(cell).trim()) : String(row ?? "").trim());
    } catch { /* Plain text production. */ }
    return true;
  }) || Object.values(attempt.photos ?? {}).some(p => p.length) || attempt.evidence.length > 0
    || Object.values(attempt.helpUsage ?? {}).some(h => h.answerSnapshot.trim());
}

export function isEmptyAbsentCopy(attempt: StudentAttempt, version: ActivityVersion | undefined) {
  const members = attempt.ownerType === "group" ? attempt.memberIdsSnapshot ?? [] : [attempt.studentId];
  return !!version && members.length > 0 && version.checkpoints.length > 0 && !hasProduction(attempt) && members.every(id =>
    attempt.attendance?.[id]?.absent && version.checkpoints.every(cp =>
      attempt.attendance?.[id]?.results[cp.id]?.source === "attendance_auto"));
}
